import React, { useEffect, useState } from "react";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import {
  HiOutlineChartBar,
  HiOutlineClipboardDocumentList,
  HiOutlineUserGroup,
  HiOutlineCalendarDays,
  HiOutlineShoppingBag,
} from "react-icons/hi2";
import Chart from "react-apexcharts";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    proposalsSent: 0,
    proposalsAccepted: 0,
    clients: 0,
    leads: 0,
    totalInvoiced: 0,
    totalDeposits: 0,
    totalCollected: 0,
  });

  const [chartData, setChartData] = useState({
    series: [{ name: "Ventas", data: [] }],
    options: {
      chart: { type: "area", toolbar: { show: false } },
      xaxis: { categories: [] },
      colors: ["#2563eb"],
      stroke: { curve: "smooth" },
    },
  });

  const [leadMetrics, setLeadMetrics] = useState(null);
  const [sellerPerformance, setSellerPerformance] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [ecommerceAlerts, setEcommerceAlerts] = useState([]);
  const formatMoney = (value = 0) =>
    Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const [events, setEvents] = useState([]);
  const [aiInsights, setAIInsights] = useState({
    totalLeads: 0,
    hotLeads: 0,
    avgScore: 0,
    topService: "-",
    conversionRate: 0,
  });

  const acknowledgeEcommerceAlerts = async () => {
    if (!ecommerceAlerts.length) return;
    try {
      await Promise.all(
        ecommerceAlerts.map((alert) =>
          updateDoc(doc(db, "notifications", alert.id), {
            read: true,
            readAt: serverTimestamp(),
          })
        )
      );
      setEcommerceAlerts([]);
    } catch (err) {
      console.error("❌ No se pudieron marcar como leídas las alertas de ecommerce:", err);
    }
  };

  // 🔹 Función auxiliar: encuentra el servicio más solicitado
  const findTopService = (leads) => {
    const serviceCount = {};
    leads.forEach((lead) => {
      (lead.cardInformation || []).forEach((service) => {
        serviceCount[service] = (serviceCount[service] || 0) + 1;
      });
    });
    const sorted = Object.entries(serviceCount).sort((a, b) => b[1] - a[1]);
    return sorted.length ? sorted[0][0] : "-";
  };
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsSnap, leadsSnap, proposalsSnap, eventsSnap, sellersSnap, timelineSnap, ecommerceNotifSnap] = await Promise.all([
          getDocs(collection(db, "clients")),
          getDocs(collection(db, "leads")),
          getDocs(collection(db, "proposalTokens")),
          getDocs(collection(db, "events")),
          getDocs(collection(db, "salesAgents")),
          getDocs(collectionGroup(db, "timeline")),
          getDocs(
            query(
              collection(db, "notifications"),
              where("type", "==", "ecommerce_order"),
              where("read", "==", false),
              orderBy("createdAt", "desc"),
              limit(5)
            )
          ),
        ]);
  
        let totalInvoiced = 0;
        let totalDeposits = 0;
        let totalCollected = 0;
        const monthlyTotals = {};
        const seenInvoices = new Set(); // 🔹 Evita duplicados
  
        const clientsMap = {};
        clientsSnap.forEach((doc) => {
          clientsMap[doc.id] = doc.data() || {};
        });

        const leadsMap = {};
        const leadsDetailed = leadsSnap.docs.map((doc) => {
          const data = doc.data() || {};
          leadsMap[doc.id] = data;
          return {
            id: doc.id,
            ...data,
          };
        });

        const sellerDocs = sellersSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const sellerEmailMap = {};
        const sellerTotals = {};
        sellerDocs.forEach((seller) => {
          const emailKey = (seller.email || "").toLowerCase();
          if (emailKey) sellerEmailMap[emailKey] = seller.id;
          sellerTotals[seller.id] = {
            id: seller.id,
            name: seller.name || "Sin nombre",
            email: seller.email || "-",
            quota: Number(seller.quota || 0),
            leads: 0,
            hot: 0,
            active: 0,
            won: 0,
            revenue: 0,
            collected: 0,
          };
        });

        leadsDetailed.forEach((lead) => {
          const emailKey = (lead.assignedToEmail || "").toLowerCase();
          const sellerId =
            (lead.assignedTo && sellerTotals[lead.assignedTo]) ||
            (emailKey && sellerTotals[sellerEmailMap[emailKey]])
              ? lead.assignedTo && sellerTotals[lead.assignedTo]
                ? lead.assignedTo
                : sellerEmailMap[emailKey]
              : null;
          if (!sellerId || !sellerTotals[sellerId]) return;
          const summary = sellerTotals[sellerId];
          summary.leads += 1;
          if ((lead.aiScore || 0) >= 80) summary.hot += 1;
          const status = (lead.status || "nuevo").toLowerCase();
          if (status === "convertido") summary.won += 1;
          if (status !== "convertido" && status !== "inactivo") summary.active += 1;
        });

        for (const clientDoc of clientsSnap.docs) {
          const clientId = clientDoc.id;
          const invoicesSnap = await getDocs(collection(db, "clients", clientId, "invoices"));

          for (const invDoc of invoicesSnap.docs) {
            const inv = invDoc.data();
  
            // 🔸 Si ya procesamos una factura igual (por total y correo), la saltamos
            const uniqueKey = `${clientId}-${inv.total}-${inv.clientEmail}`;
            if (seenInvoices.has(uniqueKey)) continue;
            seenInvoices.add(uniqueKey);
  
            const total = parseFloat(inv.total || 0);
            const deposit = parseFloat(inv.deposit || 0);
            const payments = inv.payments || [];
            const createdAt = inv.createdAt?.toDate?.() || new Date();

            totalInvoiced += total;
            totalDeposits += deposit;

            // 🔹 Repartir pagos por su mes real
            const paymentsSum = payments.reduce(
              (sum, p) => sum + parseFloat(p.amount || 0),
              0
            );

            payments.forEach((p) => {
              const parsedAmount = parseFloat(p.amount || 0);
              if (!parsedAmount) return;

              const payDate = new Date(p.date);
              const payMonthKey = payDate.toLocaleString("es-PR", { month: "short", year: "numeric" });
              monthlyTotals[payMonthKey] = (monthlyTotals[payMonthKey] || 0) + parsedAmount;
            });

            totalCollected += paymentsSum;

            // 🔹 Sumar depósito solo si no hay pagos (evita doble conteo)
            if (!payments.length && deposit > 0) {
              const monthKey = createdAt.toLocaleString("es-PR", { month: "short", year: "numeric" });
              monthlyTotals[monthKey] = (monthlyTotals[monthKey] || 0) + deposit;
              totalCollected += deposit;
            }

            const sellerKeyRaw = inv.sellerId || inv.createdBy?.uid || null;
            const sellerEmailKey = inv.createdBy?.email ? inv.createdBy.email.toLowerCase() : null;
            let sellerKey = null;
            if (sellerKeyRaw && sellerTotals[sellerKeyRaw]) {
              sellerKey = sellerKeyRaw;
            } else if (sellerEmailKey && sellerEmailMap[sellerEmailKey]) {
              sellerKey = sellerEmailMap[sellerEmailKey];
            }
            if (sellerKey && sellerTotals[sellerKey]) {
              sellerTotals[sellerKey].revenue += total;
              sellerTotals[sellerKey].collected += paymentsSum || (!payments.length ? deposit : 0);
            }
          }
        }
  
        // 🔹 Calcular propuestas aceptadas
        const proposals = proposalsSnap.docs.map((d) => d.data());
        const accepted = proposals.filter((p) =>
          ["aceptado", "aceptada"].includes(p.status)
        );
  
        // 🔹 Ordenar meses cronológicamente
        const orderedMonths = Object.keys(monthlyTotals).sort((a, b) => {
          const [ma, ya] = a.split(" ");
          const [mb, yb] = b.split(" ");
          const da = new Date(`${ma} 1, ${ya}`);
          const db = new Date(`${mb} 1, ${yb}`);
          return da - db;
        });
  
        const orderedValues = orderedMonths.map((m) => monthlyTotals[m]);
  
        // 🔹 Actualizar métricas
        setStats({
          proposalsSent: proposals.length,
          proposalsAccepted: accepted.length,
          clients: clientsSnap.size,
          leads: leadsSnap.size,
          totalInvoiced,
          totalDeposits,
          totalCollected,
        });
  
        // 🔹 Actualizar gráfica mensual
        setChartData({
          series: [{ name: "Pagos Recibidos", data: orderedValues }],
          options: {
            chart: { type: "area", toolbar: { show: false } },
            xaxis: { categories: orderedMonths },
            colors: ["#2563eb"],
            stroke: { curve: "smooth" },
          },
        });
  
        // 🔹 Cargar eventos
        const now = Date.now();
        const eventList = eventsSnap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .map((event) => {
            const rawStart =
              event.start?.toDate?.() ||
              (typeof event.start === "string" ? new Date(event.start) : null) ||
              (event.date ? new Date(event.date) : null);
            const startDate =
              rawStart instanceof Date && !isNaN(rawStart.getTime())
                ? rawStart
                : null;
            return { ...event, startDate };
          })
          .filter((event) => event.startDate && event.startDate.getTime() >= now - 24 * 3600 * 1000)
          .sort((a, b) => a.startDate - b.startDate)
          .slice(0, 6);

        setEvents(eventList);

        setSellerPerformance(
          Object.values(sellerTotals).map((stat) => ({
            ...stat,
            conversionRate: stat.leads ? ((stat.won / stat.leads) * 100).toFixed(1) : "0.0",
            progress: stat.quota ? Math.min(100, (stat.revenue / stat.quota) * 100) : null,
          }))
        );

        const feed = timelineSnap.docs
          .map((docSnap) => {
            const data = docSnap.data() || {};
            const createdAt = data.createdAt?.toDate?.() || null;
            const pathSegments = docSnap.ref.path.split("/");
            const clientsIndex = pathSegments.indexOf("clients");
            const leadsIndex = pathSegments.indexOf("leads");
            const clientIdFromPath =
              clientsIndex >= 0 && pathSegments.length > clientsIndex + 1
                ? pathSegments[clientsIndex + 1]
                : null;
            const leadIdFromPath =
              leadsIndex >= 0 && pathSegments.length > leadsIndex + 1
                ? pathSegments[leadsIndex + 1]
                : null;

            const leadData = leadIdFromPath ? leadsMap[leadIdFromPath] : null;

            const clientInfo = data.client || data.clientInfo || leadData?.clientInfo || {};
            const leadLabel =
              data.ticketTitle ||
              data.leadName ||
              data.lead ||
              leadData?.name ||
              leadData?.businessName ||
              leadData?.email ||
              leadIdFromPath ||
              "Lead";

            const clientIdResolved =
              data.clientId ||
              clientInfo.id ||
              clientIdFromPath ||
              leadData?.clientId ||
              leadData?.clientIdRef ||
              null;

            const clientData = clientIdResolved ? clientsMap[clientIdResolved] : null;

            const resolvedClientName =
              data.clientName ||
              clientInfo.businessName ||
              clientInfo.name ||
              clientInfo.clientName ||
              clientData?.businessName ||
              clientData?.name ||
              leadData?.clientName ||
              leadData?.name ||
              leadData?.businessName ||
              null;
            const resolvedClientEmail =
              data.clientEmail ||
              clientInfo.email ||
              clientInfo.contactEmail ||
              clientData?.email ||
              clientData?.contactEmail ||
              leadData?.clientEmail ||
              leadData?.email ||
              null;

            const clientReference =
              resolvedClientName ||
              resolvedClientEmail ||
              clientIdResolved ||
              null;

            return {
              id: docSnap.id,
              summary: data.summary || "Actualización en lead",
              channel: data.channel || "sistema",
              createdAt,
              lead: leadLabel,
              createdBy:
                typeof data.createdBy === "string"
                  ? data.createdBy
                  : data.createdBy?.email || data.createdBy?.name || "sistema",
              clientName: resolvedClientName,
              clientEmail: resolvedClientEmail,
              clientId: clientIdResolved,
              clientReference,
            };
          })
          .sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0))
          .slice(0, 8);
        setActivityFeed(feed);
        setEcommerceAlerts(
          ecommerceNotifSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );

      } catch (err) {
        console.error("❌ Error cargando datos del dashboard:", err);
      }
    };
  
    loadData();
  }, []);
  
  useEffect(() => {
    const loadAIInsights = async () => {
      try {
        const leadsSnap = await getDocs(collection(db, "leads"));
        const leads = leadsSnap.docs.map((d) => d.data());
  
        if (leads.length === 0) {
          setAIInsights({
            totalLeads: 0,
            hotLeads: 0,
            avgScore: 0,
            topService: "-",
            conversionRate: 0,
          });
          setLeadMetrics({
            total: 0,
            nuevo: 0,
            en_proceso: 0,
            convertido: 0,
            inactivo: 0,
            promedioScore: 0,
            tasaConversion: 0,
            fuentes: {},
          });
          return;
        }
  
        // 🔹 Total de leads y leads calientes (score >= 80)
        const totalLeads = leads.length;
        const hotLeads = leads.filter((l) => (l.aiScore || 0) >= 80).length;
        const avgScore = leads.reduce((sum, l) => sum + (l.aiScore || 0), 0) / totalLeads;
  
        // 🔹 Servicio más solicitado
        const topService = findTopService(leads);
  
        // 🔹 Métricas adicionales por estado y fuente
        const metrics = {
          total: leads.length,
          nuevo: 0,
          en_proceso: 0,
          convertido: 0,
          inactivo: 0,
          promedioScore: 0,
          tasaConversion: 0,
          fuentes: {},
        };
  
        let totalScore = 0;
  
        leads.forEach((lead) => {
          const estado = (lead.status || "nuevo").toLowerCase();
          metrics[estado] = (metrics[estado] || 0) + 1;
  
          const fuente = (lead.source || "sin fuente").toLowerCase();
          metrics.fuentes[fuente] = (metrics.fuentes[fuente] || 0) + 1;
  
          totalScore += lead.aiScore || 0;
        });
  
        metrics.promedioScore = (totalScore / leads.length).toFixed(1);
        metrics.tasaConversion = ((metrics.convertido / leads.length) * 100).toFixed(1);
        metrics.leadsList = leads;
        const conversionRate = metrics.tasaConversion;

        // 🔹 Guardar ambos estados
        setAIInsights({
          totalLeads,
          hotLeads,
          avgScore: avgScore.toFixed(1),
          topService,
          conversionRate,
          
        });
  
        setLeadMetrics(metrics);
      } catch (err) {
        console.error("❌ Error cargando insights de IA y métricas:", err);
      }
    };
  
    loadAIInsights();
  }, []);
  
  
  
  return (
    <div className="p-8 bg-gray-50 min-h-screen space-y-8">
      <h1 className="text-2xl font-semibold text-gray-800 mb-4">
        Panel General de Actividad
      </h1>

      {ecommerceAlerts.length > 0 && (
        <Link
          to="/admin/ecommerce"
          onClick={acknowledgeEcommerceAlerts}
          className="flex items-center gap-4 rounded-3xl border border-emerald-200 bg-white p-5 shadow transition hover:border-emerald-400 hover:shadow-lg"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
            <HiOutlineShoppingBag className="h-7 w-7 text-emerald-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-700">Ventas online</p>
            <h2 className="text-lg font-semibold text-gray-900">
              {ecommerceAlerts.length} pedido{ecommerceAlerts.length > 1 ? "s" : ""} pendiente{ecommerceAlerts.length > 1 ? "s" : ""}
            </h2>
            <p className="text-sm text-gray-600">
              Toca para revisar los detalles en la pestaña de e-commerce. La alerta se mantendrá hasta que la revises.
            </p>
          </div>
          <div className="text-right text-sm text-gray-500">
            <p className="font-semibold text-gray-900">
              {ecommerceAlerts[0]?.message || "Nuevo pedido capturado"}
            </p>
            <p className="text-xs">
              {ecommerceAlerts[0]?.createdAt?.toDate?.().toLocaleString?.("es-PR", {
                dateStyle: "short",
                timeStyle: "short",
              }) || ""}
            </p>
          </div>
        </Link>
      )}

      {/* MÉTRICAS PRINCIPALES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {/* Leads */}
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-blue-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Leads Totales</p>
            <p className="text-2xl font-bold text-gray-700">{leadMetrics?.total ?? stats.leads}</p>
          </div>
          <HiOutlineUserGroup className="text-blue-500 w-8 h-8" />
        </div>

        {/* Propuestas Enviadas */}
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-indigo-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Propuestas Enviadas</p>
            <p className="text-2xl font-bold text-gray-700">{stats.proposalsSent}</p>
          </div>
          <HiOutlineClipboardDocumentList className="text-indigo-500 w-8 h-8" />
        </div>

        {/* Propuestas Aceptadas */}
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-green-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Propuestas Aceptadas</p>
            <p className="text-2xl font-bold text-gray-700">{stats.proposalsAccepted}</p>
          </div>
          <HiOutlineChartBar className="text-green-500 w-8 h-8" />
        </div>

        {/* Clientes */}
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-purple-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Clientes Activos</p>
            <p className="text-2xl font-bold text-gray-700">{stats.clients}</p>
          </div>
          <HiOutlineUserGroup className="text-purple-500 w-8 h-8" />
        </div>

        {/* Ventas Totales */}
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-emerald-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Ventas Totales</p>
            <p className="text-2xl font-bold text-gray-700">
              ${formatMoney(stats.totalInvoiced)}
            </p>
          </div>
          <HiOutlineChartBar className="text-emerald-500 w-8 h-8" />
        </div>

        {/* Depósitos Registrados */}
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-yellow-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Depósitos Registrados</p>
            <p className="text-2xl font-bold text-gray-700">
              ${formatMoney(stats.totalDeposits)}
            </p>
          </div>
          <HiOutlineChartBar className="text-yellow-500 w-8 h-8" />
        </div>

        {/* Dinero Recibido */}
        <div className="bg-white p-5 rounded-lg shadow border-l-4 border-teal-500 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Dinero Recibido</p>
            <p className="text-2xl font-bold text-gray-700">
              ${formatMoney(stats.totalCollected)}
            </p>
          </div>
          <HiOutlineChartBar className="text-teal-500 w-8 h-8" />
        </div>
      </div>

      {/* GRÁFICA DE VENTAS */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-700 mb-3">
          Rendimiento de Ventas Mensual
        </h2>
        {chartData.series[0].data.length > 0 ? (
          <Chart
            options={chartData.options}
            series={chartData.series}
            type="area"
            height={300}
          />
        ) : (
          <p className="text-sm text-gray-500">No hay datos de ventas aún.</p>
        )}
      </div>

      {sellerPerformance.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Rendimiento de vendedores</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Vendedor</th>
                  <th className="px-4 py-2 text-center">Leads</th>
                  <th className="px-4 py-2 text-center">Hot</th>
                  <th className="px-4 py-2 text-center">Ganados</th>
                  <th className="px-4 py-2 text-center">Conversión</th>
                  <th className="px-4 py-2 text-right">Ventas</th>
                  <th className="px-4 py-2 text-right">Meta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sellerPerformance.map((seller) => (
                  <tr key={seller.id}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{seller.name}</p>
                      <p className="text-xs text-gray-500">{seller.email}</p>
                    </td>
                    <td className="px-4 py-3 text-center">{seller.leads}</td>
                    <td className="px-4 py-3 text-center text-orange-600">{seller.hot}</td>
                    <td className="px-4 py-3 text-center text-emerald-600">{seller.won}</td>
                    <td className="px-4 py-3 text-center">
                      {seller.conversionRate || "0.0"}%
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">
                      ${formatMoney(seller.revenue)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {seller.quota ? `$${formatMoney(seller.quota)}` : "—"}
                      {seller.progress != null && (
                        <div className="w-full bg-gray-100 rounded-full h-2 mt-1">
                          <div
                            className="h-2 rounded-full bg-orange-500"
                            style={{ width: `${seller.progress}%` }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EVENTOS Y NOTIFICACIONES */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Notificaciones */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">
            Notificaciones recientes
          </h2>
          {activityFeed.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {activityFeed.map((item) => (
                <li key={item.id} className="py-3 text-sm text-gray-600">
                  <p className="font-semibold text-gray-900">{item.summary}</p>
                  <p className="text-xs text-gray-500">
                    {item.lead ? `Lead: ${item.lead}` : "Lead sin nombre"} · Canal: {item.channel}
                  </p>
                  {item.clientReference && (
                    <p className="text-xs text-gray-500">
                      Cliente: {item.clientReference}
                      {item.clientEmail && item.clientEmail !== item.clientReference
                        ? ` · ${item.clientEmail}`
                        : ""}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    {item.createdBy ? `Por ${item.createdBy}` : "Sistema"} ·{" "}
                    {item.createdAt
                      ? item.createdAt.toLocaleString("es-PR")
                      : "Fecha desconocida"}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">Sin movimientos recientes.</p>
          )}
        </div>

{/* 🔹 Sección de Inteligencia Comercial */}
<div className="bg-white rounded-lg shadow p-6 mt-6">
  <h3 className="text-lg font-bold text-gray-700 mb-3">Inteligencia Comercial (AI)</h3>
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-600">
    <p><strong>Total Leads:</strong> {aiInsights.totalLeads}</p>
    <p><strong>Leads Calientes:</strong> {aiInsights.hotLeads}</p>
    <p><strong>Servicio Popular:</strong> {aiInsights.topService}</p>
    <p><strong>Intención Promedio:</strong> {aiInsights.avgScore}%</p>
    <p><strong>Tasa de Conversión:</strong> {aiInsights.conversionRate}%</p>
  </div>
</div>

{/* 🔸 Sección de resumen visual */}
{leadMetrics && (
  <div className="mt-8">
    <h2 className="text-lg font-semibold text-gray-700 mb-4">
      Resumen de Leads por Estado
    </h2>
    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
      {/* Total Leads */}
      <div className="bg-white p-5 rounded-lg shadow border-l-4 border-orange-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Total Leads</p>
          <p className="text-2xl font-bold text-gray-700">{leadMetrics.total}</p>
        </div>
      </div>
{/* 🔸 Seguimiento de Leads - Actividad AI */}
{leadMetrics?.leadsList?.length > 0 && (
  <div className="mt-12">
    <h2 className="text-lg font-semibold text-gray-700 mb-4">
      Seguimiento Automático de Leads (AI)
    </h2>

    <div className="bg-white rounded-lg shadow p-6 overflow-x-auto">
      <table className="min-w-full text-sm text-left text-gray-600">
        <thead className="bg-gray-100 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-2">Nombre</th>
            <th className="px-4 py-2">Canal</th>
            <th className="px-4 py-2">Score</th>
            <th className="px-4 py-2">Prioridad</th>
            <th className="px-4 py-2">Último contacto</th>
            <th className="px-4 py-2">Mensaje</th>
          </tr>
        </thead>
        <tbody>
          {leadMetrics.leadsList.map((lead, i) => (
            <tr key={i} className="border-b">
              <td className="px-4 py-2">{lead.name || "Sin nombre"}</td>
              <td className="px-4 py-2 capitalize">{lead.aiChannel || "-"}</td>
              <td className="px-4 py-2">{lead.aiScore || 0}</td>
              <td className="px-4 py-2 capitalize">{lead.prioridad || "-"}</td>
              <td className="px-4 py-2">
                {lead.lastContactAt
                  ? new Date(lead.lastContactAt.seconds * 1000).toLocaleString("es-PR")
                  : "Nunca"}
              </td>
              <td className="px-4 py-2 text-xs">
                {lead.lastContactNote || "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}


      {/* Nuevos */}
      <div className="bg-white p-5 rounded-lg shadow border-l-4 border-yellow-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Nuevos</p>
          <p className="text-2xl font-bold text-gray-700">{leadMetrics.nuevo}</p>
        </div>
      </div>

      {/* En Proceso */}
      <div className="bg-white p-5 rounded-lg shadow border-l-4 border-blue-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">En Proceso</p>
          <p className="text-2xl font-bold text-gray-700">{leadMetrics.en_proceso}</p>
        </div>
      </div>

      {/* Convertidos */}
      <div className="bg-white p-5 rounded-lg shadow border-l-4 border-green-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Convertidos</p>
          <p className="text-2xl font-bold text-gray-700">{leadMetrics.convertido}</p>
        </div>
      </div>

      {/* Inactivos */}
      <div className="bg-white p-5 rounded-lg shadow border-l-4 border-gray-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Inactivos</p>
          <p className="text-2xl font-bold text-gray-700">{leadMetrics.inactivo}</p>
        </div>
      </div>

      {/* Promedio Score */}
      <div className="bg-white p-5 rounded-lg shadow border-l-4 border-indigo-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Promedio Score</p>
          <p className="text-2xl font-bold text-gray-700">{leadMetrics.promedioScore}</p>
        </div>
      </div>

      {/* Tasa de Conversión */}
      <div className="bg-white p-5 rounded-lg shadow border-l-4 border-emerald-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Tasa de Conversión</p>
          <p className="text-2xl font-bold text-gray-700">{leadMetrics.tasaConversion}%</p>
        </div>
      </div>
    </div>
  </div>
)}



        {/* Eventos */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <HiOutlineCalendarDays className="text-blue-500" /> Próximos Eventos
          </h2>
          {events.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {events.map((e) => (
                <li key={e.id || e.title} className="py-2 text-sm text-gray-600">
                  <strong>
                    {e.startDate?.toLocaleDateString("es-PR", {
                      month: "short",
                      day: "numeric",
                    }) || "—"}
                  </strong>{" "}
                  — {e.title || "Evento sin título"}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No hay eventos registrados.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
